import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiJson } from '@/lib/apiClient';
import { tenantsQueryKey } from './useTenants';

/**
 * SLICE 14d — SWITCH RAAD SECURE ON OR OFF FOR A TENANT.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THIS IS A CUSTOMER-FACING ACT, AND THE HOOK CARRIES ONLY THE FACTS.
 * ─────────────────────────────────────────────────────────────────────────────
 * Turning it ON makes a "Block starting" control appear in a paying customer's
 * own app on their own vehicles, and a Command PIN panel in their Settings. It
 * does NOT by itself make any vehicle blockable — the dispatch gate is per-device
 * and independent. The dialog says all of that; this hook only sends the boolean
 * and surfaces the response.
 *
 * The wire contract (`api/src/modules/admin/routes.ts`, "SLICE 14d"):
 *   PATCH /api/admin/tenants/:tenantId/raad-secure  { enabled: boolean }  (.strict, no default)
 *     200 → { tenantId, raadSecureEnabled, transitioned }
 *
 * `transitioned` is the WHOLE reason the response is read rather than assumed:
 * the success copy must distinguish "you just granted this capability" from "it
 * was already on", and the caller cannot tell those apart from the boolean it
 * sent — only from whether the row moved.
 *
 * Invalidates `tenantsQueryKey` so the switch re-renders from the tenant's REAL
 * state on the next list read, never from local optimistic state alone.
 */
export interface RaadSecureResult {
  tenantId: string;
  raadSecureEnabled: boolean;
  transitioned: boolean;
}

export function useSetRaadSecure(): UseMutationResult<
  RaadSecureResult,
  Error,
  { tenantId: string; enabled: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, enabled }) =>
      apiJson<RaadSecureResult>(
        `/api/admin/tenants/${encodeURIComponent(tenantId)}/raad-secure`,
        { method: 'PATCH', body: JSON.stringify({ enabled }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantsQueryKey });
    },
  });
}
